/**
 * Design and performance space filtering.
 *
 * Each pruning operation is opt-in, and the resulting gamut is shown beside the
 * full pool so the cost of a filter is visible before it is accepted. A failing
 * filter call leaves the page as it is rather than navigating on - the user
 * would otherwise continue against a pool that was never narrowed.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    TextField, Stack, Typography, Checkbox, FormControlLabel, Box, Alert, Button,
    Card, CardContent,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import MyScatterChart from "../../components/MyScatterChart";
import LoadingComponent from "../../components/LoadingComponent";
import {
    selectionSidebar, selectionLockedRoute, isImageSelection,
} from "../../helper/selectionFlow";
import { useSelectionParams } from "../../helper/entityRoutes";

// Step 3: prune the pooled exploration samples with tickable filters and show
// the leftover gamut. Applying always starts from the full pool (backend
// reset), so re-running with different boxes never compounds cuts. There is
// no chromaticity filter here: it would remove the blacks and whites, so the
// minimum chromaticity is set on the composition step (chromatic quota only).
export default function ColorSelectionPruning() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [poolColor, setPoolColor] = useState([]);
    const [poolCount, setPoolCount] = useState(null);
    const [prunedColor, setPrunedColor] = useState(null);
    const [prunedCount, setPrunedCount] = useState(null);
    const [applied, setApplied] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const [useWidthFilter, setUseWidthFilter] = useState(true);
    const [useTimeFilter, setUseTimeFilter] = useState(true);
    const [widthLimitUm, setWidthLimitUm] = useState(60);
    // Seconds per cm²: unit-cluster time x clusters of the measured width in 1 cm².
    const [timeLimit, setTimeLimit] = useState(140);
    const [useLineCountFilter, setUseLineCountFilter] = useState(false);
    const [lineCount, setLineCount] = useState(1);

    const { selectionId, selectionType, stepRoute } = useSelectionParams();
    // The image selection keeps a width range and no marking-time filter
    // (the colour-selection script filters by width and chroma only; the
    // chroma limit is applied by the ranking).
    const imageType = isImageSelection(selectionType);
    const [widthMinUm, setWidthMinUm] = useState(10);
    useEffect(() => { if (imageType) setUseTimeFilter(false); }, [imageType]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            setLoadingMessage("Loading the pruning pool...");
            try {
                const selectionResponse = await axios.get(`api/color/selection/${selectionId}/`);
                const locked = selectionLockedRoute(
                    selectionId, selectionResponse.data.status,
                    selectionResponse.data.selection_type || selectionType,
                );
                if (locked) {
                    // Finished (or repeatability-confirmed) selections: never reset their pool.
                    navigate(locked, { replace: true });
                    return;
                }
                try {
                    const config = JSON.parse(selectionResponse.data.selection_config);
                    if (config.maximum_allowed_width) setWidthLimitUm(Math.round(config.maximum_allowed_width * 1e6) / 1000);
                    if (config.minimum_allowed_width != null) setWidthMinUm(Math.round(config.minimum_allowed_width * 1e6) / 1000);
                    if (config.maximum_time_per_cm2) setTimeLimit(config.maximum_time_per_cm2);
                } catch (e) {
                    console.error("Could not parse selection config", e);
                }
                const resetResponse = await axios.post("api/selection/samples/reset", { selectionId });
                setPoolColor(resetResponse.data.color || []);
                setPoolCount(resetResponse.data.count);
            } catch (error) {
                console.error("Error loading pruning pool:", error);
                setErrorMessage("Failed to load the sample pool for this selection.");
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    /** Resets the pool and runs the ticked filters. Resolves to true only when
     *  every call succeeded; on failure the alert stays and the caller must not
     *  move on. */
    const applyPruning = async () => {
        setLoading(true);
        setErrorMessage(null);
        try {
            setLoadingMessage("Resetting to the full sample pool...");
            const resetResponse = await axios.post("api/selection/samples/reset", { selectionId });
            let colorValues = resetResponse.data.color || [];
            setPoolColor(colorValues);
            setPoolCount(resetResponse.data.count);
            if (useWidthFilter) {
                setLoadingMessage("Applying the width filter...");
                const res = await axios.post("api/selection/filter/resolution", {
                    selectionId,
                    widthFilterValue: Number(widthLimitUm) / 1000.0,
                    ...(imageType ? { widthMinValue: Number(widthMinUm) / 1000.0 } : {}),
                });
                colorValues = res.data.color || [];
            }
            if (useTimeFilter) {
                setLoadingMessage("Applying the marking-time filter...");
                const res = await axios.post("api/selection/filter/time", {
                    selectionId,
                    maximumTimePerCm2: Number(timeLimit),
                });
                colorValues = res.data.color || [];
            }
            if (useLineCountFilter) {
                setLoadingMessage("Applying the line-count filter...");
                const res = await axios.post("api/selection/filter/linecount", {
                    selectionId,
                    lineCountValue: Number(lineCount),
                });
                colorValues = res.data.color || [];
            }
            setPrunedColor(colorValues);
            setPrunedCount(colorValues.length);
            setApplied(true);
            return true;
        } catch (error) {
            console.error("Error applying pruning:", error);
            setErrorMessage("Applying the pruning filters failed. See the console for details.");
            return false;
        } finally {
            setLoading(false);
        }
    };

    const nextPageClickFunc = async (navigatePageName) => {
        if (!applied) {
            const succeeded = await applyPruning();
            if (!succeeded) return;
        }
        navigate(navigatePageName);
    };

    const filterRow = (checked, setChecked, label, field) => (
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <FormControlLabel
                control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
                label={label}
            />
            {field}
        </Stack>
    );

    return (
        <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
            <ExplorationPageShell
                title="Design & Performance Space Filtering"
                subtitle="Tick the pruning operations to apply — the leftover gamut is shown next to the full pool."
                stepSidebar={selectionSidebar(2, prunedCount != null ? prunedCount : poolCount, selectionType)}
                footerBelowPaper={
                    <Box sx={{ width: "100%" }}>
                        <NavBarBottom
                            variant="embedded"
                            returnLabel="Back"
                            prevPage={stepRoute("explorations")}
                            nextPage={stepRoute("composition")}
                            enableNextPage={loading}
                            nextPageClickFunc={nextPageClickFunc}
                        />
                    </Box>
                }
            >
                <Stack spacing={3}>
                    {loading && <LoadingComponent message={loadingMessage} />}
                    {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

                    {/* Controls in a full-width card so nothing competes with the
                        charts for horizontal space (the shell content area is
                        narrower than the viewport breakpoints suggest). */}
                    <Card variant="outlined" sx={{ borderRadius: 3, borderColor: "rgba(15, 23, 42, 0.08)" }}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                                Pruning operations
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={3}
                                useFlexGap
                                flexWrap="wrap"
                                alignItems="center"
                            >
                                {filterRow(useWidthFilter, setUseWidthFilter, "Line width",
                                    <Stack direction="row" spacing={1.5}>
                                    {imageType && (
                                        <TextField
                                            label="Min. width (µm)"
                                            type="number"
                                            size="small"
                                            disabled={!useWidthFilter}
                                            value={widthMinUm}
                                            onChange={(e) => setWidthMinUm(e.target.value)}
                                            sx={{ width: 150 }}
                                        />
                                    )}
                                    <TextField
                                        label="Max. width (µm)"
                                        type="number"
                                        size="small"
                                        disabled={!useWidthFilter}
                                        value={widthLimitUm}
                                        onChange={(e) => setWidthLimitUm(e.target.value)}
                                        sx={{ width: 170 }}
                                    />
                                    </Stack>
                                )}
                                {filterRow(useTimeFilter, setUseTimeFilter, "Marking time",
                                    <TextField
                                        label="Max. time / cm² (s)"
                                        type="number"
                                        size="small"
                                        disabled={!useTimeFilter}
                                        value={timeLimit}
                                        onChange={(e) => setTimeLimit(e.target.value)}
                                        sx={{ width: 170 }}
                                    />
                                )}
                                {filterRow(useLineCountFilter, setUseLineCountFilter, "Line count",
                                    <TextField
                                        label="Lines per cluster"
                                        type="number"
                                        size="small"
                                        disabled={!useLineCountFilter}
                                        value={lineCount}
                                        onChange={(e) => setLineCount(e.target.value)}
                                        inputProps={{ min: 1, step: 1 }}
                                        sx={{ width: 170 }}
                                    />
                                )}
                                <Box sx={{ flexGrow: 1 }} />
                                <Button
                                    variant="contained"
                                    disableElevation
                                    startIcon={<FilterAltIcon />}
                                    onClick={() => applyPruning()}
                                    disabled={loading}
                                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                                >
                                    Apply pruning
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Stack
                        direction="row"
                        spacing={3}
                        useFlexGap
                        flexWrap="wrap"
                        justifyContent="center"
                    >
                        <Box sx={{ maxWidth: "100%" }}>
                            <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600 }}>
                                Full pool{poolCount != null ? ` (${poolCount})` : ""}
                            </Typography>
                            <MyScatterChart dataColor={poolColor} width={440} height={340} />
                        </Box>
                        <Box sx={{ maxWidth: "100%" }}>
                            <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600 }}>
                                After pruning{prunedCount != null ? ` (${prunedCount})` : ""}
                            </Typography>
                            <MyScatterChart dataColor={prunedColor || []} width={440} height={340} />
                        </Box>
                    </Stack>
                </Stack>
            </ExplorationPageShell>
        </Stack>
    );
}
